import React, { useState, useMemo, useRef, useEffect } from 'react';

import programsStyles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import styles from '../../styles/MateriasStyles/PantallaDeTareas.v2.module.css';
import Pagination from '../../components/Pagination.tsx';
import { usePermissions } from '../../hooks/usePermissions';
import { deleteStudent } from '../../api/students';

function StudentTable({
  students = [],
  loading = false,
  onSelectStudent,
  onStudentDeleted,
  readOnly = false,
  selectedCampus = '',
  serverPaging = false,
  page: serverPage = 1,
  totalPages: serverTotalPages = 1,
  onPageChange: onServerPageChange,
}) {
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const filtered = useMemo(() => {
    const campusName = String(selectedCampus || '').trim().toLowerCase();
    if (!campusName || campusName === 'todos' || campusName === 'todos los campus') {
      return students;
    }

    return students.filter((student) => {
      const values = [
        student.campus_nombre,
        student.campus,
        student.id_campus,
        student.id_campus_id,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());

      return values.includes(campusName);
    });
  }, [students, selectedCampus]);
  const totalPages = serverPaging ? Math.max(1, serverTotalPages) : Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = serverPaging ? filtered : filtered.slice((page - 1) * pageSize, page * pageSize);

  // Crear una clave única basada en los IDs de estudiantes para detectar cambios reales en la lista
  const studentsKey = useMemo(() => {
    return students.map(s => s.id_estudiante || s.id).sort().join(',');
  }, [students]);
  
  // Guardar referencia del key anterior
  const prevStudentsKeyRef = useRef(studentsKey);
  
  // Reiniciar página a 1 solo cuando realmente cambia la lista de estudiantes (nuevos IDs o filtros diferentes)
  useEffect(() => {
    if (prevStudentsKeyRef.current !== studentsKey) {
      setPage(1);
      prevStudentsKeyRef.current = studentsKey;
    }
  }, [studentsKey]);
  
  // Ajustar página si quedó fuera de rango después de un cambio
  useEffect(() => {
    if (!serverPaging && page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages, serverPaging]);
  // Eliminar goToPage, usaremos Pagination global

  // Eliminar estudiante
  const handleDeleteStudent = async (student) => {
    if (!student.id_estudiante) {
      window.alert('No se puede inactivar: ID no encontrado');
      return;
    }
    const confirm = window.confirm(
      `¿Seguro que deseas inactivar al estudiante "${student.nombre_completo}"? El estudiante se ocultará de la aplicación pero sus datos se mantendrán en la base de datos.`
    );
    if (!confirm) return;
    try {
      await deleteStudent(student.id_estudiante);
      window.alert('Estudiante inactivado exitosamente');
      if (onStudentDeleted) {
        onStudentDeleted(student.id_estudiante);
      }
    } catch (err) {
      window.alert(
        'Error al inactivar estudiante: ' + (err?.response?.data?.error || err.message)
      );
    }
  };

  // PERMISOS
  const { canEdit, canDelete } = usePermissions();
  // Usar el módulo correcto para estudiantes; en modo readOnly se deshabilitan edición y eliminación
  const puedeEditar = !readOnly && canEdit('Control Escolar');
  const puedeEliminar = !readOnly && canDelete('Control Escolar');

  const gridCols = 'minmax(210px,1fr) minmax(120px,0.8fr) minmax(140px,1fr) minmax(140px,1fr) minmax(140px,1fr) 120px';

  // Botones de acción (copiados de ProgramsTable)
  const ActionButtons = ({ onView, onEdit, onDelete }) => (
    <div className={programsStyles.actionButtons}>
      <button aria-label="Ver detalles" className={programsStyles.actionButton} onClick={onView}>
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/a1782ac5b190a9d29d8f798383eedda3f294d56b?placeholderIfAbsent=true"
          alt="Ver"
          className={programsStyles.actionIcon}
        />
      </button>
      {puedeEditar && (
        <button aria-label="Editar" className={programsStyles.actionButton} onClick={onEdit}>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true"
            alt="Editar"
            className={programsStyles.actionIcon}
          />
        </button>
      )}
      {puedeEliminar && (
        <button aria-label="Eliminar" className={programsStyles.actionButton} onClick={onDelete}>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true"
            alt="Eliminar"
            className={programsStyles.actionIcon}
          />
        </button>
      )}
      {!puedeEditar && !puedeEliminar && (
        <span style={{ color: '#888', fontSize: '12px' }}>Solo lectura</span>
      )}
    </div>
  );

  const Header = () => (
    <div className={styles.tableHeader}>
      <div className={styles.tableTitle}>
        <div className={styles.tableTitleIcon}>
          <div className={styles.iconContainerOrange}><span>🎓</span></div>
        </div>
        <h2 className={styles.tableTitleText}>Estudiantes</h2>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.contentWrapper} style={{ width: '100%', maxWidth: '100%', margin: '0 auto', padding: '0 12px' }}>
        <div className={styles.tableContainer} style={{ position: 'relative', zIndex: 1 }}>
          <Header />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentWrapper} style={{ width: '100%', maxWidth: '100%', margin: '0 auto', padding: '0 12px' }}>
      <div className={styles.tableContainer} style={{ position: 'relative', minHeight: '320px', zIndex: 1 }}>
      <Header />

      <div className={styles.tableHeaderRow} style={{ gridTemplateColumns: gridCols, padding: '12px 12px' }}>
        <span className={styles.columnHeader}>Nombre</span>
        <span className={styles.columnHeader}>Matrícula</span>
        <span className={styles.columnHeader}>Campus</span>
        <span className={styles.columnHeader}>Programa</span>
        <span className={styles.columnHeader}>Ciclo Escolar</span>
        <span className={styles.columnHeaderWide + ' ' + styles.stickyActionsHeader}>Acciones</span>
      </div>

      <div className={styles.tableBody} style={{ padding: '0 12px' }}>
        {paginated.length === 0 ? (
          <div className={styles.tableRow} style={{ gridTemplateColumns: gridCols }}>
            <span className={styles.cellData} colSpan={6}>No se encontraron estudiantes</span>
          </div>
        ) : paginated.map((student, idx) => (
          <div
            key={student.id_estudiante || idx}
            className={styles.tableRow}
            style={{ gridTemplateColumns: gridCols, cursor: 'pointer' }}
          >
            <span className={styles.cellData}>{student.nombre_completo || 'Sin nombre'}</span>
            <span className={styles.cellData}>{student.matricula || 'Sin matrícula'}</span>
            <span className={styles.cellData}>{student.campus_nombre || 'Sin campus'}</span>
            <span className={styles.cellData}>{student.programa_nombre || 'Sin programa'}</span>
            <span className={styles.cellData}>{student.ciclo_escolar_nombre || 'Sin ciclo'}</span>
            <div className={programsStyles.actionButtons + ' ' + programsStyles.stickyActionsCell}>
              <ActionButtons
                onView={() => onSelectStudent && onSelectStudent(student, 'view')}
                onEdit={() => !readOnly && onSelectStudent && onSelectStudent(student, 'edit')}
                onDelete={() => handleDeleteStudent(student)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Paginación global */}
      {totalPages > 1 && (
        <Pagination
          page={serverPaging ? serverPage : page}
          totalPages={totalPages}
          onPageChange={serverPaging ? onServerPageChange : setPage}
          className={styles.paginationWrapper}
        />
      )}
      </div>
    </div>
  );
}

export default StudentTable;
